import React, { useEffect, useState } from "react";
import { Plus, Trash2, ArrowUp, ArrowDown, Save } from "lucide-react";
import api, { mediaUrl } from "@/lib/api";
import { toast } from "sonner";
import { PAYMENT_ICON_OPTIONS, DynIcon } from "@/lib/icons";
import MediaUploader from "./MediaUploader";

const inputCls = "w-full bg-[#141414] border border-[#333] rounded-lg h-11 px-3 text-[#F0EDEA] focus:border-[#E60000] outline-none";
const label = "block text-[#A8A2A0] text-xs uppercase tracking-wide mb-1 mt-4";

function Section({ title, children }) {
  return (
    <div className="bg-[#1C1C1C] border border-[#333] rounded-2xl p-5 mb-4">
      <h2 className="font-heading font-bold uppercase text-[#F0EDEA] mb-1">{title}</h2>
      {children}
    </div>
  );
}

export default function AdminSettings() {
  const [s, setS] = useState(null);

  useEffect(() => { api.get("/admin/settings").then(({ data }) => setS(data)).catch(() => {}); }, []);
  if (!s) return <div className="text-[#A8A2A0]">Loading...</div>;

  const set = (k, v) => setS({ ...s, [k]: v });

  const save = async () => {
    try { const { data } = await api.put("/admin/settings", s); setS(data); toast.success("Settings saved"); }
    catch { toast.error("Save failed"); }
  };

  const detectChat = async () => {
    try {
      await api.put("/admin/settings", s);
      const { data } = await api.post("/admin/telegram/detect");
      if (data.ok) { setS({ ...s, telegram_bot_chat_id: String(data.chat.id) }); toast.success(`Detected: ${data.chat.title || data.chat.id}`); }
      else toast.error(data.detail || "No chat found");
    } catch (e) { toast.error("Detect failed"); }
  };

  const testTelegram = async () => {
    try { await api.put("/admin/settings", s); await api.post("/admin/telegram/test"); toast.success("Test sent — check Telegram"); }
    catch (e) { toast.error(e.response?.data?.detail || "Test failed"); }
  };

  // payment methods
  const pm = s.payment_methods || [];
  const setPm = (list) => set("payment_methods", list);
  const updPm = (i, k, v) => setPm(pm.map((m, idx) => idx === i ? { ...m, [k]: v } : m));
  const addPm = () => setPm([...pm, { id: `m${Date.now()}`, name: "New Method", icon: "wallet", fee_pct: 0, enabled: true }]);
  const delPm = (i) => setPm(pm.filter((_, idx) => idx !== i));
  const movePm = (i, d) => { const a = [...pm]; const j = i + d; if (j < 0 || j >= a.length) return; [a[i], a[j]] = [a[j], a[i]]; setPm(a); };

  // why list
  const why = s.why_list || [];
  const setWhy = (list) => set("why_list", list);

  return (
    <div data-testid="admin-settings" className="pb-4">
      <div className="flex items-center justify-between mb-4">
        <h1 className="font-heading font-black uppercase text-xl text-[#F0EDEA]">Site Settings</h1>
        <button onClick={save} className="btn-skew btn-primary text-xs" data-testid="save-settings-btn"><span><Save size={16} /> Save</span></button>
      </div>

      <Section title="Links">
        <label className={label}>Signal Link</label>
        <input className={inputCls} value={s.signal_link || ""} onChange={(e) => set("signal_link", e.target.value)} data-testid="set-signal" />
        <label className={label}>Telegram Link</label>
        <input className={inputCls} value={s.telegram_link || ""} onChange={(e) => set("telegram_link", e.target.value)} data-testid="set-telegram" />
        <label className={label}>Telegram Channel Link (Notify Me)</label>
        <input className={inputCls} value={s.telegram_channel_link || ""} onChange={(e) => set("telegram_channel_link", e.target.value)} data-testid="set-channel" />
      </Section>

      <Section title="Telegram Order Bot">
        <p className="text-[#A8A2A0] text-xs mb-1">Every new order is auto-sent to your Telegram via bot <b>@RedEyeOrders_bot</b>.</p>
        <label className={label}>Order Telegram Link (shown after checkout)</label>
        <input className={inputCls} value={s.telegram_order_link || ""} onChange={(e) => set("telegram_order_link", e.target.value)} data-testid="set-tg-order-link" />
        <label className={label}>Bot Destination Chat ID</label>
        <input className={inputCls} value={s.telegram_bot_chat_id || ""} onChange={(e) => set("telegram_bot_chat_id", e.target.value)} data-testid="set-tg-chat-id" />
        <p className="text-[#A8A2A0] text-xs mt-1">Use <b>@channelusername</b> (add the bot as an admin there) or a numeric chat ID. To auto-fill: message <b>@RedEyeOrders_bot</b> (or add it to your channel and post once), then tap <b>Detect chat ID</b>.</p>
        <div className="flex gap-2 mt-3">
          <button onClick={detectChat} className="btn-skew btn-secondary flex-1 text-xs" data-testid="tg-detect-btn"><span>Detect chat ID</span></button>
          <button onClick={testTelegram} className="btn-skew btn-primary flex-1 text-xs" data-testid="tg-test-btn"><span>Send test</span></button>
        </div>
      </Section>

      <Section title="Homepage">
        <label className={label}>Hours</label>
        <input className={inputCls} value={s.hours || ""} onChange={(e) => set("hours", e.target.value)} data-testid="set-hours" />
        <label className={label}>Tagline</label>
        <textarea className={`${inputCls} h-20 py-2`} value={s.homepage_tagline || ""} onChange={(e) => set("homepage_tagline", e.target.value)} data-testid="set-tagline" />
        <label className={label}>Why RED EYE List</label>
        <div className="space-y-2">
          {why.map((w, i) => (
            <div key={i} className="flex gap-2">
              <input className={inputCls} value={w} onChange={(e) => setWhy(why.map((x, idx) => idx === i ? e.target.value : x))} data-testid={`set-why-${i}`} />
              <button onClick={() => setWhy(why.filter((_, idx) => idx !== i))} className="text-[#A8A2A0] hover:text-[#E60000]"><Trash2 size={16} /></button>
            </div>
          ))}
          <button onClick={() => setWhy([...why, ""])} className="flex items-center gap-1 text-[#E60000] text-sm font-semibold" data-testid="add-why"><Plus size={16} /> Add item</button>
        </div>
        <label className={label}>Logo</label>
        <div className="flex items-center gap-3">
          <img src={mediaUrl(s.logo_url)} alt="logo" className="h-10 bg-black rounded px-2" />
          <div className="flex-1"><MediaUploader value={[]} onChange={(m) => m[0] && set("logo_url", m[0].path)} /></div>
        </div>
      </Section>

      <Section title="Delivery & Payments">
        <label className={label}>Local Delivery Fee ($ per order)</label>
        <input type="number" className={inputCls} value={s.delivery_fee ?? 0} onChange={(e) => set("delivery_fee", Number(e.target.value))} data-testid="set-delivery-fee" />
        <label className={label}>Payment Methods</label>
        <div className="space-y-2">
          {pm.map((m, i) => (
            <div key={m.id} className="bg-[#141414] border border-[#333] rounded-lg p-2 flex items-center gap-2 flex-wrap" data-testid={`pm-${i}`}>
              <DynIcon name={m.icon} size={18} className="text-[#E60000]" />
              <input className="bg-[#0f0f0f] border border-[#333] rounded h-9 px-2 text-[#F0EDEA] w-28" value={m.name} onChange={(e) => updPm(i, "name", e.target.value)} data-testid={`pm-name-${i}`} />
              <select className="bg-[#0f0f0f] border border-[#333] rounded h-9 px-2 text-[#F0EDEA]" value={m.icon} onChange={(e) => updPm(i, "icon", e.target.value)}>
                {PAYMENT_ICON_OPTIONS.map((ic) => <option key={ic} value={ic}>{ic}</option>)}
              </select>
              <div className="flex items-center gap-1">
                <input type="number" className="bg-[#0f0f0f] border border-[#333] rounded h-9 px-2 text-[#F0EDEA] w-16" value={m.fee_pct} onChange={(e) => updPm(i, "fee_pct", Number(e.target.value))} data-testid={`pm-fee-${i}`} />
                <span className="text-[#A8A2A0] text-sm">%</span>
              </div>
              <button onClick={() => updPm(i, "enabled", !m.enabled)} className={`text-xs px-2 py-1 rounded border ${m.enabled ? "border-green-600 text-green-400" : "border-[#333] text-[#A8A2A0]"}`} data-testid={`pm-enabled-${i}`}>{m.enabled ? "On" : "Off"}</button>
              <button onClick={() => movePm(i, -1)} className="text-[#A8A2A0]"><ArrowUp size={15} /></button>
              <button onClick={() => movePm(i, 1)} className="text-[#A8A2A0]"><ArrowDown size={15} /></button>
              <button onClick={() => delPm(i)} className="text-[#A8A2A0] hover:text-[#E60000]"><Trash2 size={15} /></button>
            </div>
          ))}
          <button onClick={addPm} className="flex items-center gap-1 text-[#E60000] text-sm font-semibold" data-testid="add-pm"><Plus size={16} /> Add method</button>
        </div>
      </Section>

      <Section title="Email Alerts">
        <p className="text-[#A8A2A0] text-xs mb-2">Order alerts send via Resend when configured, otherwise fall back to Emergent-managed email.</p>
        <label className={label}>From Name</label>
        <input className={inputCls} value={s.from_name || ""} onChange={(e) => set("from_name", e.target.value)} data-testid="set-from-name" />
        <label className={label}>From Email</label>
        <input className={inputCls} value={s.from_email || ""} onChange={(e) => set("from_email", e.target.value)} data-testid="set-from-email" />
        <label className={label}>Alert Recipient Email</label>
        <input className={inputCls} value={s.alert_email || ""} onChange={(e) => set("alert_email", e.target.value)} data-testid="set-alert-email" />
      </Section>

      <button onClick={save} className="btn-skew btn-primary w-full"><span><Save size={16} /> Save All Settings</span></button>
    </div>
  );
}
